import React from "react";
import type { SVGProps } from "react";
import styles from "./ProfileEmptyState.module.scss";
import { ReactComponent as IllustrationProjects } from "../../../../assets/svg/project-empty-state.svg";
import { ReactComponent as IllustrationMaterials } from "../../../../assets/svg/materials-empty-state.svg";
import { ReactComponent as IllustrationProducts } from "../../../../assets/svg/products-empty-state.svg";
import { ReactComponent as IllustrationReviews } from "../../../../assets/svg/review-empty-state.svg";
import { ReactComponent as IllustrationMuro } from "../../../../assets/svg/wall-empty-state.svg";
import { ReactComponent as IllustrationAccesos } from "../../../../assets/svg/misPostulacionesEmpty.svg";
import { ReactComponent as IllustrationDocumentation } from "../../../../assets/svg/empty-project.svg";
import { ReactComponent as IllustrationGeneric } from "../../../../assets/svg/EmptyState-dashboard.svg";

export type ProfileEmptyIllustration =
  | "projects"
  | "materials"
  | "products"
  | "reviews"
  | "muro"
  | "accesos"
  | "documentation"
  | "generic";

type SvgIllustration = React.FC<SVGProps<SVGSVGElement>>;

const ILLUSTRATIONS: Record<ProfileEmptyIllustration, SvgIllustration> = {
  projects: IllustrationProjects,
  materials: IllustrationMaterials,
  products: IllustrationProducts,
  reviews: IllustrationReviews,
  muro: IllustrationMuro,
  accesos: IllustrationAccesos,
  documentation: IllustrationDocumentation,
  generic: IllustrationGeneric,
};

export interface ProfileEmptyStateProps {
  illustration: ProfileEmptyIllustration;
  title: string;
  description?: string;
  primaryAction?: { label: string; onClick: () => void };
  onRootClick?: () => void;
  compact?: boolean;
  className?: string;
}

const ProfileEmptyState: React.FC<ProfileEmptyStateProps> = ({
  illustration,
  title,
  description,
  primaryAction,
  onRootClick,
  compact = false,
  className = "",
}) => {
  const Svg = ILLUSTRATIONS[illustration];
  const rootClass = [
    styles.root,
    compact ? styles.root_compact : "",
    onRootClick ? styles.root_clickable : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const illuClass = [styles.illustration, compact ? styles.illustration_compact : ""]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      <div className={styles.visual}>
        <Svg className={illuClass} aria-hidden />
      </div>
      <div className={styles.content}>
        <h3 className={styles.title}>{title}</h3>
        {description ? <p className={styles.description}>{description}</p> : null}
        {primaryAction ? (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={(e) => {
                e.stopPropagation();
                primaryAction.onClick();
              }}
            >
              {primaryAction.label}
            </button>
          </div>
        ) : null}
      </div>
    </>
  );

  if (onRootClick) {
    return (
      <div
        className={rootClass}
        onClick={onRootClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onRootClick();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {inner}
      </div>
    );
  }

  return <div className={rootClass}>{inner}</div>;
};

export default ProfileEmptyState;
